import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";

import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { trackEvent, EventType } from "@/shared/utils/core";
import { LAWYER_PLANS } from "@/shared/constants/lawyerPlans";

import type { LawyerPlanKey } from "@/shared/constants/lawyerPlans";
import type { Database } from "@/shared/types/database.types";

type LawyerClientInvitationRow = Database["public"]["Tables"]["lawyer_client_invitations"]["Row"];

export interface SendClientInvitationParams {
  clientEmail: string;
  clientFirstName: string;
  clientLastName: string;
  clientPhone?: string;
  recommendedPlan: LawyerPlanKey | null;
  firstClientOfferApplied: boolean;
  firmName?: string;
}

export function useLawyerClientInvitations() {
  const { user } = useAppContext();
  const queryClient = useQueryClient();

  const {
    data: invitations = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["lawyer-client-invitations", user?.user_id],
    queryFn: async (): Promise<LawyerClientInvitationRow[]> => {
      if (!user?.user_id) return [];

      const { data, error: queryError } = await supabase
        .from("lawyer_client_invitations")
        .select("*")
        .eq("lawyer_user_id", user.user_id)
        .order("created_at", { ascending: false });

      if (queryError) throw queryError;

      return data ?? [];
    },
    enabled: !!user?.user_id,
    staleTime: 2 * 60 * 1000,
  });

  const sendClientInvitation = useMutation({
    mutationFn: async (params: SendClientInvitationParams) => {
      if (!user?.user_id) {
        throw new Error("User not authenticated");
      }

      if (user.email && params.clientEmail.toLowerCase() === user.email.toLowerCase()) {
        throw new Error("You cannot invite yourself");
      }

      const invitationToken = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      let planPriceCents = params.recommendedPlan
        ? params.firstClientOfferApplied
          ? LAWYER_PLANS[params.recommendedPlan].firstClientPrice * 100
          : LAWYER_PLANS[params.recommendedPlan].price * 100
        : null;

      const trimmedFirstName = params.clientFirstName.trim();
      const trimmedLastName = params.clientLastName.trim();

      // Create invitation first (without estate_id)
      const { data: invitation, error: insertError } = await supabase
        .from("lawyer_client_invitations")
        .insert({
          lawyer_user_id: user.user_id,
          client_email: params.clientEmail.toLowerCase().trim(),
          client_first_name: trimmedFirstName,
          client_last_name: trimmedLastName,
          client_phone: params.clientPhone ?? null,
          invitation_token: invitationToken,
          recommended_plan: params.recommendedPlan,
          plan_price_cents: planPriceCents,
          first_client_offer_applied: params.firstClientOfferApplied,
          expires_at: expiresAt.toISOString(),
        })
        .select()
        .single();

      if (insertError) {
        throw new Error(`Failed to create invitation: ${insertError.message}`);
      }

      // Create estate + link + update invitation atomically via RPC
      const { data: estateId, error: estateError } = await supabase.rpc("create_intake_estate", {
        p_invitation_id: invitation.id,
      });

      if (estateError) {
        throw new Error(`Failed to create estate record: ${estateError.message}`);
      }

      invitation.estate_id = estateId;

      if (params.firstClientOfferApplied) {
        const { data: updated } = await supabase
          .from("lawyers")
          .update({
            first_client_used: true,
            first_client_invitation_id: invitation.id,
          })
          .eq("user_id", user.user_id)
          .eq("first_client_used", false)
          .select();

        if (!updated || updated.length === 0) {
          const fullPriceCents = params.recommendedPlan
            ? LAWYER_PLANS[params.recommendedPlan].price * 100
            : null;

          const { error: rollbackError } = await supabase
            .from("lawyer_client_invitations")
            .update({
              first_client_offer_applied: false,
              plan_price_cents: fullPriceCents,
            })
            .eq("id", invitation.id);

          if (rollbackError) {
            throw new Error("Failed to update invitation pricing");
          }

          planPriceCents = fullPriceCents;
          invitation.first_client_offer_applied = false;
        }
      }

      const attorneyName = user.full_name ?? user.first_name ?? "Your attorney";
      const inviteLink = `${window.location.origin}/client-invite?token=${invitationToken}`;

      const { error: emailError } = await supabase.functions.invoke("communications/email/send", {
        body: {
          type: "lawyer_client_invite",
          to: params.clientEmail,
          user_id: user.user_id,
          data: {
            first_name: trimmedFirstName,
            attorney_name: attorneyName,
            firm_name: params.firmName ?? "",
            client_first_name: trimmedFirstName,
            plan_name: params.recommendedPlan
              ? LAWYER_PLANS[params.recommendedPlan].name
              : "No recommendation",
            plan_price: planPriceCents !== null ? `$${planPriceCents / 100}` : null,
            cta_url: inviteLink,
          },
          metadata: {
            invitation_id: invitation.id,
            recommended_plan: params.recommendedPlan,
          },
        },
      });

      if (emailError) {
        console.error("Failed to send client invitation email:", emailError);
      }

      // TODO: logActivity once the client accepts and an estate_id exists

      trackEvent(EventType.INVITE_SENT, {
        invite_type: "lawyer_client",
        recommended_plan: params.recommendedPlan ?? "none",
        first_client_offer: params.firstClientOfferApplied,
      });

      supabase.functions
        .invoke("communications/notify-client-invite", {
          body: { invitationId: invitation.id },
        })
        .catch(() => {});

      return invitation;
    },
    onSuccess: () => {
      enqueueSnackbar("Invitation sent", { variant: "success" });
      queryClient.invalidateQueries({
        queryKey: ["lawyer-client-invitations", user?.user_id],
      });
    },
    onError: () => {
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
    },
  });

  const resendClientInvitation = useMutation({
    mutationFn: async ({ invitationId }: { invitationId: number }) => {
      if (!user?.user_id) {
        throw new Error("User not authenticated");
      }

      const newToken = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const { data: updated, error: updateError } = await supabase
        .from("lawyer_client_invitations")
        .update({
          invitation_token: newToken,
          expires_at: expiresAt.toISOString(),
        })
        .eq("id", invitationId)
        .eq("status", "pending")
        .select();

      if (updateError) {
        throw new Error("Failed to update invitation");
      }

      if (!updated || updated.length === 0) {
        throw new Error("This invitation can no longer be resent");
      }

      const invitation = updated[0];

      const attorneyName = user.full_name ?? user.first_name ?? "Your attorney";
      const inviteLink = `${window.location.origin}/client-invite?token=${newToken}`;

      const { data: lawyerRecord } = await supabase
        .from("lawyers")
        .select("firm_name")
        .eq("user_id", user.user_id)
        .maybeSingle();

      const { error: emailError } = await supabase.functions.invoke("communications/email/send", {
        body: {
          type: "lawyer_client_invite_reminder",
          to: invitation.client_email,
          user_id: user.user_id,
          data: {
            first_name: invitation.client_first_name ?? invitation.client_email.split("@")[0],
            attorney_name: attorneyName,
            firm_name: lawyerRecord?.firm_name ?? "",
            client_first_name:
              invitation.client_first_name ?? invitation.client_email.split("@")[0],
            plan_name: invitation.recommended_plan
              ? (LAWYER_PLANS[invitation.recommended_plan as LawyerPlanKey]?.name ?? null)
              : null,
            cta_url: inviteLink,
          },
          metadata: {
            invitation_id: invitationId,
            resend: true,
          },
        },
      });

      if (emailError) {
        console.error("Failed to resend client invitation email:", emailError);
      }

      trackEvent(EventType.LAWYER_CLIENT_INVITE_RESENT, {
        invitation_id: invitationId,
        estate_id: invitation.estate_id,
        email_sent: !emailError,
      });

      return invitation;
    },
    onSuccess: (data) => {
      enqueueSnackbar(`Invitation resent to ${data.client_email}`, { variant: "success" });
      queryClient.invalidateQueries({
        queryKey: ["lawyer-client-invitations", user?.user_id],
      });
    },
    onError: () => {
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
    },
  });

  return {
    invitations,
    isLoading,
    error,
    refetch,
    sendClientInvitation,
    resendClientInvitation,
    isSending: sendClientInvitation.isPending,
  };
}
